<script lang="ts" setup>
import { computed, nextTick, useTemplateRef, watch } from 'vue';
import { IconArrowUp, IconPlayerStop } from '@/icons';
import { UiButtonIcon } from '../ui-button-icon';

interface Props {
  modelValue?: string;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  sendLabel?: string;
  stopLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  disabled: false,
  loading: false,
  placeholder: '',
  sendLabel: '',
  stopLabel: '',
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  submit: [];
  stop: [];
}>();

defineSlots<{
  leadingActions?: () => unknown;
  footer?: () => unknown;
}>();

const textareaRef = useTemplateRef<HTMLTextAreaElement>('textarea');
const canSubmit = computed(() => props.modelValue.trim().length > 0 && !props.disabled && !props.loading);

function resizeTextarea() {
  const textarea = textareaRef.value;
  if (!textarea) {
    return;
  }
  textarea.style.height = '0px';
  textarea.style.height = `${Math.min(textarea.scrollHeight, 152)}px`;
}

watch(
  () => props.modelValue,
  () => nextTick(resizeTextarea),
  { immediate: true },
);

function onInput(event: Event) {
  emit('update:modelValue', (event.target as HTMLTextAreaElement).value);
}

function onKeydown(event: KeyboardEvent) {
  if (event.key !== 'Enter' || event.shiftKey) {
    return;
  }
  event.preventDefault();
  if (props.loading) {
    emit('stop');
    return;
  }
  if (canSubmit.value) {
    emit('submit');
  }
}
</script>

<template>
  <div :class="$style.root" data-ui-component="UiAiChatComposer">
    <div :class="$style.inputShell">
      <textarea
        ref="textarea"
        :value="props.modelValue"
        :placeholder="props.placeholder"
        :disabled="props.disabled"
        :class="$style.textarea"
        rows="1"
        @input="onInput"
        @keydown="onKeydown"
      />
      <div :class="$style.controls">
        <div v-if="$slots.leadingActions" :class="$style.leadingActions">
          <slot name="leadingActions" />
        </div>
        <UiButtonIcon
          :variant="props.loading ? 'secondary' : 'primary'"
          :disabled="!props.loading && !canSubmit"
          :aria-label="props.loading ? props.stopLabel : props.sendLabel"
          @click="props.loading ? emit('stop') : emit('submit')"
        >
          <IconPlayerStop v-if="props.loading" />
          <IconArrowUp v-else />
        </UiButtonIcon>
      </div>
    </div>
    <div v-if="$slots.footer" :class="$style.footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<style lang="scss" module>
@use '../../styles/typography';

.root {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.inputShell {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0;
  min-height: 160px;
  padding: 16px 20px 40px;
  border-radius: 16px;
  border: 1px solid var(--ui2-stroke-primary, #d5d6d9);
  background: var(--ui2-background-primary, #fff);
  box-shadow: 0 0 12px 0 var(--ui2-shade-black-4, rgba(0, 0, 0, 0.04));
}

.textarea {
  width: 100%;
  min-height: 28px;
  max-height: 152px;
  padding: 0;
  margin: 0;
  border: 0;
  resize: none;
  outline: none;
  background: transparent;
  color: var(--ui2-content-primary, #010000);
  @include typography.style('body-1');
  line-height: 22px;
  caret-color: var(--ui2-content-primary, #010000);

  &::placeholder {
    color: var(--ui2-content-tertiary, #978f91);
  }
}

.controls {
  position: absolute;
  right: 12px;
  bottom: 12px;
  left: 12px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  pointer-events: none;
}

.leadingActions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
  pointer-events: all;
}

.controls :global([data-ui-component='UiButtonIcon']) {
  flex: none;
  pointer-events: all;
}

.footer {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  color: var(--ui2-content-secondary, #655e60);
  @include typography.style('data-xs-regular');
}
</style>
